import { TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useRef } from 'react';

import { Card } from '@/components/ui/card';

type NewsItem = {
  category: string;
  percentChange: number;
  headline: string;
};

const mockNews: NewsItem[] = [
  {
    category: 'CL',
    percentChange: 3.12,
    headline:
      'Prețurile petrolului cresc pe fondul îngrijorărilor privind aprovizionarea',
  },
  {
    category: 'XLF',
    percentChange: -1.87,
    headline: 'Acțiunile bancare scad din cauza îngrijorărilor de reglementare',
  },
  {
    category: 'ICLN',
    percentChange: 4.56,
    headline: 'Investițiile în energie regenerabilă ating noi maxime',
  },
  {
    category: 'XRT',
    percentChange: 1.23,
    headline: 'Datele privind cheltuielile consumatorilor depășesc așteptările',
  },
  {
    category: 'EURUSD',
    percentChange: 0.45,
    headline: 'Banca Centrală majorează dobânzile cu 25 de puncte de bază',
  },
  {
    category: 'QQQ',
    percentChange: 2.34,
    headline: 'Sectorul tech crește pe fondul rezultatelor solide din T4',
  },
  {
    category: 'BTC',
    percentChange: 5.43,
    headline: 'Piețele crypto arată semne de revenire',
  },
  {
    category: 'VNQ',
    percentChange: -2.15,
    headline: 'Sectorul imobiliar se confruntă cu provocări',
  },
  {
    category: 'XLV',
    percentChange: 3.21,
    headline: 'Acțiunile din sănătate câștigă după aprobările FDA',
  },
  {
    category: 'EEM',
    percentChange: 1.67,
    headline: 'Piețele emergente depășesc performanța țărilor dezvoltate',
  },
  {
    category: 'GLD',
    percentChange: 2.89,
    headline: 'Aurul atinge un nou maxim istoric',
  },
  {
    category: 'XLI',
    percentChange: 0.78,
    headline: 'PMI din industrie semnalează expansiune',
  },
  {
    category: 'TSLA',
    percentChange: -1.45,
    headline: 'Vânzările de vehicule electrice încetinesc pe piețele cheie',
  },
  {
    category: 'JPM',
    percentChange: 1.92,
    headline: 'Băncile mari raportează creștere puternică a creditării',
  },
  {
    category: 'NG',
    percentChange: 2.67,
    headline: 'Prețurile gazelor naturale cresc pe cererea de iarnă',
  },
  {
    category: 'DBA',
    percentChange: -0.89,
    headline:
      'Contractele futures pe cereale scad pe perspectiva unei recolte bune',
  },
];

function NewsCard({ item }: { item: NewsItem }) {
  const isPositive = item.percentChange >= 0;

  return (
    <Card className="w-48 flex-shrink-0 border-neutral-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className="rounded bg-neutral-100 px-2 py-0.5 text-xs font-bold text-neutral-700">
          {item.category}
        </span>
        <div
          className={`flex items-center gap-1 text-sm font-semibold ${
            isPositive ? 'text-green-primary' : 'text-red-primary'
          }`}>
          {isPositive ? (
            <TrendingUp className="h-4 w-4" />
          ) : (
            <TrendingDown className="h-4 w-4" />
          )}
          <span>
            {isPositive ? '+' : ''}
            {item.percentChange.toFixed(2)}%
          </span>
        </div>
      </div>
      <p className="text-sm font-semibold leading-snug text-neutral-800">
        {item.headline}
      </p>
    </Card>
  );
}

function NewsColumn({
  items,
  speed,
  direction = 'up',
}: {
  items: NewsItem[];
  speed: number;
  direction?: 'up' | 'down';
}) {
  // Quadruple items for seamless loop - ensures half the content (2 copies) fills the container
  const duplicatedItems = [...items, ...items, ...items, ...items];
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerRef}
      className="relative h-full w-52 flex-shrink-0 overflow-hidden">
      <div
        ref={contentRef}
        className={`flex flex-col items-center gap-4 ${direction === 'up' ? 'animate-scroll-up' : 'animate-scroll-down'}`}
        style={{
          animationDuration: `${speed}s`,
        }}>
        {duplicatedItems.map((item, index) => (
          <NewsCard key={`${item.category}-${index}`} item={item} />
        ))}
      </div>
    </div>
  );
}

export function AnimatedNewsCards() {
  // Split news into three columns
  const leftColumnNews = mockNews.filter((_, i) => i % 3 === 0);
  const middleColumnNews = mockNews.filter((_, i) => i % 3 === 1);
  const rightColumnNews = mockNews.filter((_, i) => i % 3 === 2);
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full items-stretch justify-center overflow-hidden bg-neutral-50 py-8 pl-0 pr-8">
      {/* Gradient overlays for smooth fade effect */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-32 bg-gradient-to-b from-neutral-50 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-32 bg-gradient-to-t from-neutral-50 to-transparent" />

      {/* Left column - bottom to top */}
      <NewsColumn items={leftColumnNews} speed={60} direction="up" />

      {/* Middle column - top to bottom */}
      <NewsColumn items={middleColumnNews} speed={75} direction="down" />

      {/* Right column - bottom to top, hidden on lg, visible on xl+ */}
      <div className="hidden h-full xl:block">
        <NewsColumn items={rightColumnNews} speed={68} direction="up" />
      </div>
    </div>
  );
}
